import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import { TagEditor } from '../components/TagEditor';
import type { OutreachProfileDto } from '../outreachTypes';
import type { ProfileUpdate } from '../hooks/useProfileActions';
import { profileMessages as messages } from './profileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileTagsCardProps {
  profile: OutreachProfileDto;
  onSave: (patch: ProfileUpdate) => void;
  disabled: boolean;
}

const TEMPO = { slow: messages.tempoSlow, mid: messages.tempoMid, fast: messages.tempoFast } as const;

/** Genres and moods (design outreach b1). The matches use the first 3 genres. */
const ProfileTagsCardComponent: React.FC<ProfileTagsCardProps> = ({ profile, onSave, disabled }) => {
  const intl = useIntl();
  const saveGenres = useCallback((genres: string[]) => onSave({ genres }), [onSave]);
  const saveMoods = useCallback((moods: string[]) => onSave({ moods }), [onSave]);

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(messages.genresTitle)}
        right={<span className={styles.small}>{intl.formatMessage(messages.genresMeta)}</span>}
      />
      <PanelBody>
        <div className={styles.stack}>
          <TagEditor
            values={profile.genres}
            onChange={saveGenres}
            addLabel={intl.formatMessage(messages.addGenre)}
            highlightFirst={3}
            max={8}
            disabled={disabled}
            testId="profile-genres"
          />
          <TagEditor
            values={profile.moods}
            onChange={saveMoods}
            addLabel={intl.formatMessage(messages.addMood)}
            max={6}
            disabled={disabled}
            testId="profile-moods"
          />
          {profile.tempoBand !== null && (
            <p className={styles.small}>
              {intl.formatMessage(messages.tempo, { band: intl.formatMessage(TEMPO[profile.tempoBand]) })}
            </p>
          )}
        </div>
      </PanelBody>
    </Panel>
  );
};

export const ProfileTagsCard = TypedMemo(ProfileTagsCardComponent);
